import BigButton from "components/atoms/buttons/BigButton";
import { UnderlineText } from "components/atoms/text/Text";
import { COLORS } from "styles/colors";
import { ReactComponent as PasskeyIcon } from "assets/icons/passkey.svg";
import useStatusMutation from "hook/useStatusMutation";
import {
  ChallengeResponse,
  GetLoginResponse,
} from "api/interfaces/authInterface.interface";
import useApiError from "hook/useApiError";
import { CustomError } from "api/interfaces/commonInterface.interface";
import * as s from "../../LoginStyled";
import { postVerifyChallenge, postVerifyCredential } from "api/authAPI";
import {
  createUuid,
  decordeStringToArrayBuffer,
  getClientDevice,
} from "utils/functions";
import { verifyCredential } from "utils/AuthUtil";
import { LOGIN_STEP, LOGIN_TAB } from "../../LoginContainer";
import { ReactComponent as FingerPrintIcon } from "assets/icons/finger_print.svg";
import { useRef } from "react";

export const LoginPasskey = ({
  onLoginSuccess,
  onChangeStep,
}: {
  onLoginSuccess: (data: GetLoginResponse) => void;
  onChangeStep: (step: LOGIN_STEP) => void;
}) => {
  const { handleErrorNotify, lang } = useApiError();

  const isLoading = useRef<boolean>(false);
  const mutationRegisterCredential = useStatusMutation(postVerifyCredential, {
    onSuccess: (data: GetLoginResponse) => {
      onLoginSuccess(data);
      isLoading.current = false;
    },
    onError: (e: CustomError) => {
      handleErrorNotify(e as CustomError, lang.label.passkey.loginFailed);
      isLoading.current = false;
    },
  });

  const muationGetChallenge = useStatusMutation(postVerifyChallenge, {
    onSuccess: async (data: ChallengeResponse) => {
      if (!data.result || data.error !== 0) {
        handleErrorNotify(
          data.error as unknown as CustomError,
          lang.label.passkey.loginFailed
        );
        isLoading.current = false;
        return;
      }
      const { challenge, rpId, timeout, deviceName, sessionName } = data.result;
      const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions =
        {
          challenge: decordeStringToArrayBuffer(challenge),
          rpId: rpId,
          timeout: timeout,
          userVerification: "required",
        };
      try {
        const { credentialId, authenticatorData, clientDataJSON, signature } =
          await verifyCredential(publicKeyCredentialRequestOptions);
        mutationRegisterCredential.mutate({
          sessionName,
          deviceName,
          credentialId,
          authenticatorData,
          clientDataJSON,
          signature,
        });
      } catch (e: any) {
        isLoading.current = false;
        if (e.message) {
          if (e.message.includes("not allowed")) {
            return;
          }
          const csError = {
            response: {
              data: {
                error: e?.message ?? 110003,
              },
            },
            status: 500,
            headers: "",
          };
          handleErrorNotify(
            csError as unknown as CustomError,
            lang.label.passkey.loginFailed,
            "PasskeyErrorCode"
          );
          return;
        }
        handleErrorNotify(e as CustomError, lang.label.passkey.loginFailed);
        return;
      }
    },
    onError: (e: CustomError) => {
      handleErrorNotify(e as CustomError, lang.label.passkey.loginFailed);
      isLoading.current = false;
    },
  });

  const onLoginWithPasskey = async () => {
    if (isLoading.current) return;
    isLoading.current = true;
    const deviceName = getClientDevice();
    const sessionName = createUuid(new Date().getTime().toString());
    muationGetChallenge.mutate({
      deviceName,
      sessionName,
    });
  };
  return (
    <s.PassKeyBox>
      <s.LoginInfo tab={LOGIN_TAB.PASSKEY}>
        <FingerPrintIcon />
        <h3>Sign In With Passkey</h3>
        <p>
          Use your device’s biometric authentication or security key to sign in
          securely without a password.
        </p>
      </s.LoginInfo>
      <BigButton
        onClickBtn={onLoginWithPasskey}
        iconComponent={<PasskeyIcon />}
        label="Login with Passkey"
        data-testid="auth-signin-passkey-button-login-with-passkey"
      />

      <s.PasskeySetupLink>
        Don’t have one? You can{" "}
        <UnderlineText
          id="dw_login_button_passkey"
          fontSize={14}
          color={COLORS.BUTTONS2}
          onClick={() => {
            onChangeStep(LOGIN_STEP.SETUP_PASSKEY);
          }}
          data-testid="auth-signin-passkey-button-passkey-change-step"
        >
          Setup Passkey
        </UnderlineText>
      </s.PasskeySetupLink>
    </s.PassKeyBox>
  );
};
